/**
 * editCommand — ScheduleDraft 编辑命令与校验结果的领域锚点。
 *
 * 本模块定义 UI 无关、传输无关的编辑命令语言，让 DataGrid、弹窗、侧栏和未来
 * Agent 自动化入口共享同一条校验、重算、确认、mutation 与缓存刷新管道。
 * 它是后续约束反馈、冲突提示和撤销能力的前端领域边界。
 *
 * 设计文档 → docs/30_editing_publish/30_local_edit_and_undo.md
 * 骨架关联 → hooks/useScheduleDraftEditPipeline.js (命令执行管道), lib/editCommandPipeline.js (ChangeSet 构建), lib/validation/index.js (校验归一化), context/ScheduleDraftEditorWorkspaceContext.jsx (执行入口)
 */

/** Single field edit for one flight. */
export const UPDATE_FLIGHT_FIELD = "UPDATE_FLIGHT_FIELD";

/** Batch field edit for one flight. */
export const UPDATE_FLIGHT_FIELDS = "UPDATE_FLIGHT_FIELDS";

/** Batch field edit for multiple flights. */
export const BATCH_UPDATE_FLIGHT_FIELDS = "BATCH_UPDATE_FLIGHT_FIELDS";

/** Move a flight across time/resource dimensions. */
export const MOVE_FLIGHT = "MOVE_FLIGHT";

/** Apply engine-calculated downstream flight times. */
export const AUTO_CALCULATE_FLIGHTS = "AUTO_CALCULATE_FLIGHTS";

/** Apply engine-calculated curfew adjustments. */
export const AUTO_ADJUST_CURFEW = "AUTO_ADJUST_CURFEW";

/** Apply engine-calculated turnaround adjustments. */
export const AUTO_ADJUST_TURNAROUND = "AUTO_ADJUST_TURNAROUND";

/** Apply batch time offset with engine preview. */
export const BATCH_SHIFT = "BATCH_SHIFT";

/** Apply batch airport change with route rematch and engine preview. */
export const BATCH_CHANGE_AIRPORT = "BATCH_CHANGE_AIRPORT";

/** Apply batch route-line switch with route rematch and engine preview. */
export const BATCH_SWITCH_LINE = "BATCH_SWITCH_LINE";

/** Add one flight to the draft local working copy. */
export const ADD_FLIGHT = "ADD_FLIGHT";

/** Add multiple flights to the draft local working copy. */
export const BATCH_ADD_FLIGHTS = "BATCH_ADD_FLIGHTS";

/** Delete one flight from the draft. */
export const DELETE_FLIGHT = "DELETE_FLIGHT";

/** Delete multiple flights from the draft local working copy. */
export const BATCH_DELETE_FLIGHTS = "BATCH_DELETE_FLIGHTS";

/** Import flights from a selected source into the draft. */
export const IMPORT_SOURCE = "IMPORT_SOURCE";

/** Reserved command for adopting reference-track flights. */
export const ADOPT_SOURCE_ITEMS = "ADOPT_SOURCE_ITEMS";

/** Reserved command for generating candidate schedule plans. */
export const GENERATE_CANDIDATES = "GENERATE_CANDIDATES";

/** Reserved command for evaluating the current draft. */
export const EVALUATE_DRAFT = "EVALUATE_DRAFT";

/**
 * All supported edit command type values.
 *
 * @type {readonly string[]}
 */
export const EDIT_COMMAND_TYPES = Object.freeze([
    UPDATE_FLIGHT_FIELD,
    UPDATE_FLIGHT_FIELDS,
    BATCH_UPDATE_FLIGHT_FIELDS,
    MOVE_FLIGHT,
    AUTO_CALCULATE_FLIGHTS,
    AUTO_ADJUST_CURFEW,
    AUTO_ADJUST_TURNAROUND,
    BATCH_SHIFT,
    BATCH_CHANGE_AIRPORT,
    BATCH_SWITCH_LINE,
    ADD_FLIGHT,
    BATCH_ADD_FLIGHTS,
    DELETE_FLIGHT,
    BATCH_DELETE_FLIGHTS,
    IMPORT_SOURCE,
    ADOPT_SOURCE_ITEMS,
    GENERATE_CANDIDATES,
    EVALUATE_DRAFT,
]);

export const VALIDATION_SEVERITY_ERROR = "error";
export const VALIDATION_SEVERITY_WARNING = "warning";
export const VALIDATION_SEVERITY_INFO = "info";

/**
 * Standard validation severities ordered from most to least restrictive.
 *
 * @type {readonly string[]}
 */
export const VALIDATION_SEVERITIES = Object.freeze([
    VALIDATION_SEVERITY_ERROR,
    VALIDATION_SEVERITY_WARNING,
    VALIDATION_SEVERITY_INFO,
]);

/**
 * @typedef {Object} EditCommand
 * @property {string} type Command type, usually one of EDIT_COMMAND_TYPES.
 * @property {string|number} draftId Target schedule draft id.
 * @property {string|number=} flightId Target flight id for flight-scoped commands.
 * @property {Object} payload Command-specific input values.
 * @property {Object=} meta Optional UI, trace, optimistic update, or conflict metadata.
 */

/**
 * @typedef {Object} ValidationIssue
 * @property {string} scope Issue scope, such as "draft", "flight", "field", or "source".
 * @property {string|number=} entityId Entity id within the scope.
 * @property {string=} fieldKey Field key when the issue is field-specific.
 * @property {string} severity Issue severity, usually "error", "warning", or "info".
 * @property {string} code Stable machine-readable issue code.
 * @property {string} message Human-readable issue description.
 * @property {boolean} blocking Whether this issue must stop the commit.
 * @property {string} source Issue producer, such as "client", "server", or a rule id.
 */

/**
 * @typedef {Object} EditPipelineResult
 * @property {"success"|"blocked"|"canceled"} status Pipeline terminal status.
 * @property {EditCommand} command Original command submitted to the pipeline.
 * @property {EditCommand=} recalculatedCommand Command after client-side recalculation.
 * @property {*=} response Raw mutation response returned by the executor.
 * @property {ValidationIssue[]} issues Client and server validation issues collected by the pipeline.
 */

/**
 * Returns whether a value is a recognized command type.
 *
 * @param {string} type Candidate command type.
 * @returns {boolean} True when the type is known to this command model.
 */
export function isEditCommandType(type) {
    return EDIT_COMMAND_TYPES.includes(type);
}

/**
 * Performs a shallow structural check for an edit command.
 *
 * This is not a domain validation step. It only protects the pipeline from
 * malformed inputs before rule execution begins.
 *
 * @param {*} command Candidate command object.
 * @returns {boolean} True when the command has the required top-level shape.
 */
export function isEditCommand(command) {
    return Boolean(
        command
            && typeof command === "object"
            && isEditCommandType(command.type)
            && command.draftId !== undefined
            && command.draftId !== null
            && command.payload !== undefined,
    );
}
